import { createContext, forwardRef, useContext, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Slot } from "radix-ui";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { cn } from "../lib/cn";

export const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-all duration-200 ease-out-expo focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-[0.98] select-none",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow-[0_1px_0_rgb(255_255_255/0.15)_inset,0_8px_20px_-10px_rgb(146_114_42/0.7)] hover:bg-aegold-700 hover:shadow-[0_12px_28px_-12px_rgb(146_114_42/0.8)] hover:-translate-y-px",
        gold: "bg-gradient-gold text-white shadow-gold hover:brightness-110 hover:-translate-y-px",
        secondary: "bg-sand-200 text-aeblack-900 hover:bg-sand-300",
        outline: "border border-sand-300 bg-white/70 text-foreground hover:bg-sand-100 hover:border-aegold-300",
        ghost: "text-foreground hover:bg-sand-100 hover:text-aegold-800",
        link: "text-aegold-700 underline-offset-4 hover:underline",
        destructive: "bg-danger-600 text-white hover:bg-danger-700",
        success: "bg-success-600 text-white hover:bg-success-700",
        dark: "bg-aeblack-950 text-white hover:bg-aeblack-800",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-12 rounded-xl px-6 text-base",
        xl: "h-13 rounded-2xl px-7 text-base",
        icon: "size-10",
        "icon-sm": "size-8 rounded-md",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

/**
 * A screen shows one primary action. Inside a `secondary` scope the primary styles (default and gold) render as outline buttons,
 * so only a button marked `primary` stands out; dialogs and side panels open a `primary` scope of their own, where their submit
 * button is the one primary action of that surface.
 */
const Emphasis = createContext<"primary" | "secondary">("primary");

export function ActionScope({ emphasis, children }: { emphasis: "primary" | "secondary"; children: ReactNode }) {
  return <Emphasis.Provider value={emphasis}>{children}</Emphasis.Provider>;
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  /** The screen's one primary action: keeps its primary style inside a secondary scope. */
  primary?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, primary = false, children, disabled, ...props }, ref) => {
    const scope = useContext(Emphasis);
    const strong = variant == null || variant === "default" || variant === "gold";
    const shown = strong && scope === "secondary" && !primary ? "outline" : variant;
    const marker = strong && shown !== "outline" ? { "data-primary-action": "" } : {};
    if (asChild) {
      return (
        <Slot.Root ref={ref} className={cn(buttonVariants({ variant: shown, size }), className)} {...marker} {...props}>
          {children}
        </Slot.Root>
      );
    }
    return (
      <button ref={ref} className={cn(buttonVariants({ variant: shown, size }), className)} disabled={disabled || loading} {...marker} {...props}>
        {loading ? <Loader2 className="animate-spin" /> : null}
        {children}
      </button>
    );
  },
);
Button.displayName = "Button";
